import React from "react";
import { PhysicalKind, type CastMode, type ColumnModel } from "target_wasm";
import { useBoundStore } from "components/canvas/stores/useAppStore";
import { showNotificationGlobal } from "stores/useNotificationStore";
import { askConfirm } from "stores/useConfirmStore";
import { openColumnProperties } from "stores/useColumnPropertiesStore";
import type { OverlayInitialView } from "./entities";

export interface DisplaySettingsPatch {
  timezone?: string | null;
  decimalPlaces?: number | null;
  thousandSeparator?: string | null;
  timestampFormat?: string | null;
  dateFormat?: string | null;
  /** Duration render unit wire name (DurationDisplayUnit::as_wire_str:
   *  "Microseconds" | "Seconds" | "Minutes" | "Hours" | "Days" | "Years"). */
  durationDisplayUnit?: string | null;
  linkEnabled?: boolean | null;
}

/** A column's edits, from any surface: each reports its own failure. */
function attempt(what: string, act: () => void, notify = true): boolean {
  const clientApp = useBoundStore.getState().clientApp;
  if (!clientApp) return false;
  try {
    act();
    return true;
  } catch (error) {
    console.error(`Failed to ${what}:`, error);
    if (notify) showNotificationGlobal(`Failed to ${what}`, "error");
    return false;
  }
}
const client = () => useBoundStore.getState().clientApp!;

export const lockColumnType = (
  tableId: string,
  columnId: string,
  locked: boolean,
): boolean =>
  attempt(
    "toggle column lock",
    () => client().toggle_column_dtype_lock(columnId, tableId, locked),
    false,
  );

export const deleteColumn = (tableId: string, columnId: string): boolean =>
  attempt("delete column", () =>
    client().handle_soft_delete_table_column(tableId, columnId),
  );

export const castColumn = (
  tableId: string,
  columnId: string,
  dataType: PhysicalKind,
  mode: CastMode,
  newName?: string,
): void => {
  attempt("cast column", () =>
    client().handle_cast_table_column(
      tableId,
      columnId,
      dataType,
      mode,
      newName,
    ),
  );
};

const FOLLOW_UP: Partial<Record<PhysicalKind, OverlayInitialView>> = {
  [PhysicalKind.Categorical]: "categories",
  [PhysicalKind.TimestampUTCSeconds]: "display",
  [PhysicalKind.Date]: "display",
  [PhysicalKind.Duration]: "display",
};

export const setEmptyColumnType = (
  tableId: string,
  columnId: string,
  dataType: PhysicalKind,
  locked: boolean,
): boolean =>
  attempt("set data type", () =>
    client().handle_set_empty_column_data_type(
      tableId,
      columnId,
      dataType,
      locked,
    ),
  );

/** A type that takes settings opens them next, where the picker was. */
export const setColumnDataType = (
  tableId: string,
  columnId: string,
  dataType: PhysicalKind,
  locked: boolean,
  where: { viewId?: string; anchor?: HTMLElement } = {},
): void => {
  const view = FOLLOW_UP[dataType];
  if (setEmptyColumnType(tableId, columnId, dataType, locked) && view)
    openColumnProperties({ tableId, columnId, view, ...where });
};

export const setDisplaySettings = (
  tableId: string,
  columnId: string,
  patch: DisplaySettingsPatch,
): void => {
  attempt("set display settings", () => {
    const [col] = client().column_models(tableId, [columnId]) as ColumnModel[];
    const pick = <T,>(next: T | null | undefined, current: T | undefined) =>
      next === undefined ? (current ?? undefined) : (next ?? undefined);
    client().set_column_display_settings(
      tableId,
      columnId,
      pick(patch.timezone, col?.display_timezone),
      pick(patch.decimalPlaces, col?.decimal_places),
      pick(patch.thousandSeparator, col?.thousand_separator),
      pick(patch.timestampFormat, col?.timestamp_format),
      pick(patch.dateFormat, col?.date_format),
      pick(patch.durationDisplayUnit, col?.duration_display_unit),
      patch.linkEnabled === undefined
        ? (col?.link_enabled ?? false)
        : (patch.linkEnabled ?? false),
    );
  });
};

export const extractUnique = (tableId: string, columnIds: string[]): void => {
  if (columnIds.length === 0) return;
  attempt("extract unique values", () =>
    client().handle_extract_unique_values(tableId, columnIds),
  );
};

/** Deletes at once, unless computed columns depend on it: they cascade with
 *  it, so they are named and the delete waits for a yes. */
export function requestDeleteColumn(tableId: string, columnId: string): void {
  let dependents: string[] = [];
  try {
    dependents = client().computed_dependents_of(tableId, columnId);
  } catch (err) {
    console.error("computed_dependents_of failed:", err);
  }
  if (dependents.length === 0) {
    deleteColumn(tableId, columnId);
    return;
  }
  askConfirm({
    title: "Delete column with dependents",
    confirmText: "Delete",
    onConfirm: () => deleteColumn(tableId, columnId),
    message: (
      <div data-testid="delete-column-dialog-message">
        <p>
          {dependents.length}{" "}
          {dependents.length === 1 ? "computed column" : "computed columns"}{" "}
          depend on this column and will be deleted along with it:
        </p>
        <ul className="mt-1 list-disc pl-5 text-fg-medium">
          {dependents.map((name, i) => (
            <li key={`${name}-${i}`} data-testid="delete-column-dependent-row">
              {name}
            </li>
          ))}
        </ul>
        <p className="mt-2 text-fg-muted-regular-small">
          You can restore them from Deleted columns — a computed column only
          once the columns it references are back.
        </p>
      </div>
    ),
  });
}
